/**
 * 🏠 USER HUB - Unified user management dashboard
 * Consolidates all user management functions with role-based views
 * Replaces scattered user/driver/verification/approval pages
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Dashboard,
  People,
  VerifiedUser,
  Assignment,
  TrendingUp,
  Refresh,
  Download,
  FilterList,
  Add,
  Notifications,
  Analytics,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

// Import components (we'll create these next)
import { UnifiedUserDirectory } from './components/UnifiedUserDirectory';
import { UnifiedApprovalPipeline } from './components/UnifiedApprovalPipeline';
import { OnboardingTracker } from './components/OnboardingTracker';
import { UserActionsCenter } from './components/UserActionsCenter';
import { UserProfileStats } from '@/components/admin/UserProfileStats';
import { adminApiService } from '@/services/adminApiService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-hub-tabpanel-${index}`}
      aria-labelledby={`user-hub-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const UserHub: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    activeRenters: 0,
    activeHosts: 0,
    activeDrivers: 0,
    multiRoleUsers: 0,
    pendingVerifications: 0,
    followUpRequired: 0,
    newToday: 0
  });
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    phone: '',
    userType: 'renter' as 'renter' | 'host' | 'driver',
    location: ''
  });

  // Refs to trigger refresh in child components
  const userDirectoryRef = useRef<{ refreshData: () => void }>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Fetch user statistics from API
  const fetchUserStats = async () => {
    try {
      const response = await adminApiService.getUsers({ limit: 1 });

      if (response.success && response.data.statistics) {
        const stats = response.data.statistics;

        // Get all users to calculate role distribution
        const allUsersResponse = await adminApiService.getUsers({ limit: 10000 });
        const allUsers = allUsersResponse.data.users || [];

        // Calculate role counts
        const roleStats = allUsers.reduce((acc, user) => {
          const roles = user.roles || [user.userType];
          roles.forEach(role => {
            if (role === 'renter') acc.renters++;
            if (role === 'host') acc.hosts++;
            if (role === 'driver') acc.drivers++;
          });
          return acc;
        }, { renters: 0, hosts: 0, drivers: 0 });

        setUserStats({
          totalUsers: stats.total || 0,
          activeRenters: roleStats.renters,
          activeHosts: roleStats.hosts,
          activeDrivers: roleStats.drivers,
          multiRoleUsers: stats.multiRoleUsers || 0,
          pendingVerifications: stats.pendingVerification || 0,
          followUpRequired: 0, // TODO: Add follow-up count from backend
          newToday: stats.registeredToday || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch user statistics:', error);
      // Keep existing stats or defaults
    }
  };

  // Load statistics on mount
  useEffect(() => {
    fetchUserStats();
  }, []);

  const handleRefreshData = async () => {
    setIsLoading(true);
    try {
      // Increment refresh key to trigger re-render in child components
      setRefreshKey(prev => prev + 1);

      // Fetch fresh statistics
      await fetchUserStats();

      // Trigger refresh in active tab component
      if (currentTab === 0 && userDirectoryRef.current) {
        userDirectoryRef.current.refreshData();
      }

      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdvancedFilters = () => {
    setAdvancedFiltersOpen(true);
  };

  const handleExportData = async () => {
    try {
      setIsLoading(true);

      // Prepare export data based on current tab
      const exportType = ['users', 'approvals', 'onboarding', 'actions', 'stats'][currentTab];

      // Create CSV/JSON export
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `polarride_${exportType}_${timestamp}.csv`;

      // TODO: Get actual data from child components
      // For now, create a basic CSV structure
      const csvContent = `data:text/csv;charset=utf-8,Export Type,${exportType}\nExport Date,${new Date().toLocaleString()}\n\nNote: Full export functionality requires backend API integration`;

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${exportType} data`);
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = () => {
    setAddUserDialogOpen(true);
  };

  const handleCreateUser = async () => {
    // Validate required fields
    if (!newUserData.name || !newUserData.email || !newUserData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      const result = await adminApiService.createUser(newUserData);

      if (result.success) {
        toast.success('User created successfully');
        setAddUserDialogOpen(false);
        // Reset form
        setNewUserData({
          name: '',
          email: '',
          phone: '',
          userType: 'renter',
          location: ''
        });
        // Refresh data
        await fetchUserStats();
        if (userDirectoryRef.current) {
          userDirectoryRef.current.refreshData();
        }
      } else {
        toast.error(result.error || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Failed to create user');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            User Hub
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Unified user management across all roles and verification stages
          </Typography>
        </Box>
        
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            sx={{ textTransform: 'none' }}
            onClick={handleAdvancedFilters}
          >
            Advanced Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            sx={{ textTransform: 'none' }}
            onClick={handleExportData}
            disabled={isLoading}
          >
            Export Data
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ textTransform: 'none' }}
            onClick={handleAddUser}
          >
            Add User
          </Button>
          <Tooltip title="Refresh all data">
            <IconButton onClick={handleRefreshData} disabled={isLoading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {userStats.totalUsers.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
                <People sx={{ fontSize: 40, color: 'primary.main', opacity: 0.3 }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                <Chip label={`+${userStats.newToday} today`} size="small" color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {userStats.multiRoleUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Multi-Role Users
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: 'success.main', opacity: 0.3 }} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Users with 2+ active roles
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                    {userStats.pendingVerifications}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Verifications
                  </Typography>
                </Box>
                <VerifiedUser sx={{ fontSize: 40, color: 'warning.main', opacity: 0.3 }} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Require admin review
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {userStats.followUpRequired}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Follow-up Required
                  </Typography>
                </Box>
                <Notifications sx={{ fontSize: 40, color: 'error.main', opacity: 0.3 }} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                High priority items
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Role Distribution */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Role Distribution
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Chip 
              label={`${userStats.activeRenters.toLocaleString()} Renters`} 
              color="primary" 
              variant="outlined" 
            />
            <Chip 
              label={`${userStats.activeHosts.toLocaleString()} Hosts`} 
              color="secondary" 
              variant="outlined" 
            />
            <Chip 
              label={`${userStats.activeDrivers.toLocaleString()} Drivers`} 
              color="success" 
              variant="outlined" 
            />
            <Chip 
              label={`${userStats.multiRoleUsers} Multi-Role`} 
              color="warning" 
              variant="outlined" 
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Alert Section */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Multi-Role Support:</strong> Users can now have multiple active roles (Renter + Host + Driver). 
          Use the smart filters to find users by role combinations.
        </Typography>
      </Alert>

      {/* Main Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="user hub tabs">
            <Tab 
              label="User Directory" 
              icon={<People />} 
              iconPosition="start"
              sx={{ textTransform: 'none', minHeight: 48 }}
            />
            <Tab 
              label="Approval Pipeline" 
              icon={<VerifiedUser />} 
              iconPosition="start"
              sx={{ textTransform: 'none', minHeight: 48 }}
            />
            <Tab 
              label="Onboarding Tracker" 
              icon={<Assignment />} 
              iconPosition="start"
              sx={{ textTransform: 'none', minHeight: 48 }}
            />
            <Tab 
              label="Actions Center" 
              icon={<Dashboard />} 
              iconPosition="start"
              sx={{ textTransform: 'none', minHeight: 48 }}
            />
            <Tab 
              label="Profile Stats" 
              icon={<Analytics />} 
              iconPosition="start"
              sx={{ textTransform: 'none', minHeight: 48 }}
            />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <UnifiedUserDirectory />
        </TabPanel>
        
        <TabPanel value={currentTab} index={1}>
          <UnifiedApprovalPipeline />
        </TabPanel>
        
        <TabPanel value={currentTab} index={2}>
          <OnboardingTracker />
        </TabPanel>
        
        <TabPanel value={currentTab} index={3}>
          <UserActionsCenter />
        </TabPanel>
        
        <TabPanel value={currentTab} index={4}>
          <UserProfileStats />
        </TabPanel>
      </Card>

      {/* Advanced Filters Dialog */}
      <Dialog
        open={advancedFiltersOpen}
        onClose={() => setAdvancedFiltersOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Advanced Filters</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Advanced filters are available directly in the User Directory tab below.
              Use the filter section to narrow down users by role, status, location, and more.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Navigate to the "User Directory" tab to access comprehensive filtering options including:
            </Typography>
            <Box component="ul" sx={{ mt: 2 }}>
              <li>Role-based filtering (Renter, Host, Driver, Multi-role)</li>
              <li>Verification status filtering</li>
              <li>Location-based filtering</li>
              <li>Date range filtering</li>
              <li>Profile completion percentage</li>
              <li>Last activity tracking</li>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAdvancedFiltersOpen(false);
            setCurrentTab(0); // Switch to User Directory tab
          }} variant="contained">
            Go to User Directory
          </Button>
          <Button onClick={() => setAdvancedFiltersOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog
        open={addUserDialogOpen}
        onClose={() => setAddUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Full Name"
              fullWidth
              required
              placeholder="John Doe"
              value={newUserData.name}
              onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
            />
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              required
              placeholder="john.doe@example.com"
              value={newUserData.email}
              onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
            />
            <TextField
              label="Phone Number"
              fullWidth
              required
              placeholder="+234-xxx-xxx-xxxx"
              value={newUserData.phone}
              onChange={(e) => setNewUserData({ ...newUserData, phone: e.target.value })}
            />
            <FormControl fullWidth required>
              <InputLabel>User Type</InputLabel>
              <Select
                label="User Type"
                value={newUserData.userType}
                onChange={(e) => setNewUserData({ ...newUserData, userType: e.target.value as 'renter' | 'host' | 'driver' })}
              >
                <MenuItem value="renter">Renter</MenuItem>
                <MenuItem value="host">Host</MenuItem>
                <MenuItem value="driver">Driver</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Location"
              fullWidth
              placeholder="Lagos, Nigeria"
              value={newUserData.location}
              onChange={(e) => setNewUserData({ ...newUserData, location: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={isLoading || !newUserData.name || !newUserData.email || !newUserData.phone}
          >
            {isLoading ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};